import type { EnvelopeId } from './envelope'
import { effectiveEntries, type LedgerEntry, type OccurrenceId } from './ledger'
import type { Xof } from './money'

/**
 * Calcul des soldes d'une période à partir des soldes d'ouverture et du journal (spec §3.4).
 *
 *   solde      = ouverture + affecté + transferts reçus − transferts envoyés − dépensé ± ajustements
 *   réservé    = réservations − libérations
 *   disponible = solde − réservé
 *
 * Une réservation ne réduit que le disponible. Au paiement d'une échéance, la réservation est
 * libérée et la dépense enregistrée dans la même opération : rien n'est déduit deux fois.
 */

export interface EnvelopeOpening {
  balance: Xof
  reserved: Xof
}

export interface PeriodOpening {
  unallocated: Xof
  envelopes: Readonly<Record<EnvelopeId, EnvelopeOpening>>
}

export interface EnvelopeBalance {
  openingBalance: Xof
  allocated: Xof
  transferredIn: Xof
  transferredOut: Xof
  spent: Xof
  /** Ajustements nets (crédits − débits). */
  adjusted: Xof
  /** Argent présent dans l'enveloppe. */
  balance: Xof
  /** Part du solde mise de côté pour des échéances non payées. */
  reserved: Xof
  /** Ce qui peut être dépensé maintenant. */
  available: Xof
}

export interface LedgerState {
  /** Revenus reçus pendant la période. */
  incomeReceived: Xof
  /** Argent reçu mais pas encore affecté à une enveloppe. */
  unallocated: Xof
  allocatedTotal: Xof
  spentTotal: Xof
  envelopes: Readonly<Record<EnvelopeId, EnvelopeBalance>>
  /** Montant encore réservé par échéance. */
  reservedByOccurrence: Readonly<Record<OccurrenceId, Xof>>
}

export const EMPTY_OPENING: PeriodOpening = { unallocated: 0, envelopes: {} }

function emptyBalance(opening: EnvelopeOpening = { balance: 0, reserved: 0 }): EnvelopeBalance {
  return {
    openingBalance: opening.balance,
    allocated: 0,
    transferredIn: 0,
    transferredOut: 0,
    spent: 0,
    adjusted: 0,
    balance: opening.balance,
    reserved: opening.reserved,
    available: opening.balance - opening.reserved,
  }
}

export function computeLedgerState(
  opening: PeriodOpening,
  entries: readonly LedgerEntry[],
): LedgerState {
  const envelopes: Record<EnvelopeId, EnvelopeBalance> = {}
  for (const [id, envelopeOpening] of Object.entries(opening.envelopes)) {
    envelopes[id] = emptyBalance(envelopeOpening)
  }
  const touch = (id: EnvelopeId): EnvelopeBalance => (envelopes[id] ??= emptyBalance())

  const reservedByOccurrence: Record<OccurrenceId, Xof> = {}
  let incomeReceived = 0
  let unallocated = opening.unallocated
  let allocatedTotal = 0
  let spentTotal = 0

  for (const entry of effectiveEntries(entries)) {
    switch (entry.kind) {
      case 'income':
        incomeReceived += entry.amount
        unallocated += entry.amount
        break
      case 'allocation': {
        unallocated -= entry.amount
        allocatedTotal += entry.amount
        touch(entry.envelopeId).allocated += entry.amount
        break
      }
      case 'expense':
        spentTotal += entry.amount
        touch(entry.envelopeId).spent += entry.amount
        break
      case 'transfer':
        touch(entry.sourceEnvelopeId).transferredOut += entry.amount
        touch(entry.destinationEnvelopeId).transferredIn += entry.amount
        break
      case 'reservation':
        touch(entry.envelopeId).reserved += entry.amount
        reservedByOccurrence[entry.occurrenceId] =
          (reservedByOccurrence[entry.occurrenceId] ?? 0) + entry.amount
        break
      case 'release':
        touch(entry.envelopeId).reserved -= entry.amount
        reservedByOccurrence[entry.occurrenceId] =
          (reservedByOccurrence[entry.occurrenceId] ?? 0) - entry.amount
        break
      case 'adjustment': {
        const delta = entry.direction === 'credit' ? entry.amount : -entry.amount
        if (entry.envelopeId === null) unallocated += delta
        else touch(entry.envelopeId).adjusted += delta
        break
      }
      case 'reversal':
        // Exclues par effectiveEntries : une annulation n'a pas d'effet propre.
        break
    }
  }

  for (const envelope of Object.values(envelopes)) {
    envelope.balance =
      envelope.openingBalance +
      envelope.allocated +
      envelope.transferredIn -
      envelope.transferredOut -
      envelope.spent +
      envelope.adjusted
    envelope.available = envelope.balance - envelope.reserved
  }

  return {
    incomeReceived,
    unallocated,
    allocatedTotal,
    spentTotal,
    envelopes,
    reservedByOccurrence,
  }
}

export function envelopeBalance(state: LedgerState, id: EnvelopeId): EnvelopeBalance {
  return state.envelopes[id] ?? emptyBalance()
}

/** Disponible total dans les enveloppes, distinct du non affecté (spec §8). */
export function totalAvailable(state: LedgerState, ids?: readonly EnvelopeId[]): Xof {
  const selected = ids ?? Object.keys(state.envelopes)
  return selected.reduce((total, id) => total + envelopeBalance(state, id).available, 0)
}
